<script setup lang="ts">
  import { IconImage } from '@/shared/ui';
  import CheckIcon from '@/shared/assets/icons/CheckIcon.svg';

  const props = withDefaults(
    defineProps<{
      id: number | string;
      checked: boolean;
      disabled?: boolean;
      indeterminate?: boolean;
    }>(),
    { disabled: false, indeterminate: false }
  );

  const emit = defineEmits(['toggle']);

  /**
   * The visible state always comes from the prop. Restoring the input after the
   * interaction avoids a filled box with no mark when the click does not change
   * the incoming value, which happens when the parent clears a partial selection.
   */
  const handleChange = (event: Event) => {
    if (props.disabled) {
      return;
    }

    (event.target as HTMLInputElement).checked = props.checked;
    emit('toggle');
  };
</script>

<template>
  <div
    class="relative inline-flex items-center justify-center"
    :class="disabled ? 'opacity-50 cursor-not-allowed' : ''"
  >
    <input
      :id="'type-' + id"
      type="checkbox"
      :checked="checked"
      :disabled="disabled"
      :indeterminate="indeterminate && !checked"
      class="w-4 h-4 border-lightgray-xx rounded-sm appearance-none bg-transparent border border-light-gray-xx checked:bg-[var(--theme-secondary)] indeterminate:bg-[var(--theme-secondary)] indeterminate:border-[var(--theme-secondary-ink)] relative-z-10 disabled:cursor-not-allowed"
      @change="handleChange"
    >
    <span
      v-if="checked"
      class="absolute flex items-center justify-center pointer-events-none h-3 inset-0.5"
    >
      <IconImage
        :src="CheckIcon"
        alt="Checked"
        class="text-white"
      />
    </span>
    <span
      v-else-if="indeterminate"
      class="absolute inset-0 flex items-center justify-center pointer-events-none"
      data-testid="checkbox-indeterminate-mark"
    >
      <span class="h-0.5 w-2 rounded-full bg-white-01" />
    </span>
  </div>
</template>
